/**
 * Warranted Minimum Availability — O&M dashboard tile.
 *
 * Renders the Availability Guarantee from an O&M contract's extracted terms,
 * tracks the operator's reported cumulative EPEBA against the guaranteed level
 * over the current Operational Year, and shows liquidated-damages exposure vs
 * the cap. Operators report monthly; figures are entered/edited here.
 *
 * Data:
 *   GET    /api/contracts/<id>/om/availability   — computed dashboard payload
 *   GET    /api/contracts/<id>/om/reports        — raw reports (for the editable table)
 *   POST   /api/contracts/<id>/om/reports        — add a monthly figure
 *   DELETE /api/contracts/<id>/om/reports/<rid>  — remove a figure
 *
 * The contractual EPEBA can't be recomputed from AEMO data (it needs plant
 * SCADA + PVsyst + the revenue meter), so the operator's reported number is the
 * input until a Tier-2 recompute exists.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, AlertTitle, Chip, Tooltip,
  LinearProgress, Table, TableHead, TableBody, TableRow, TableCell, IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

import KpiCard, { fmtAUDCompact } from './KpiCard';
import { defaultLineLegendProps } from './charts/shared';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const AMBER = '#D98A2B';
const STATUS = {
  on_track: { color: '#22c55e', label: 'On track' },
  at_risk:  { color: '#f59e0b', label: 'At risk' },
  breach:   { color: '#ef4444', label: 'Breach' },
  unknown:  { color: '#94a3b8', label: 'No data' },
};

const fmtPct = (frac, d = 2) => (frac == null ? '—' : `${(frac * 100).toFixed(d)}%`);
const fmtPP  = (frac, d = 2) => (frac == null ? '—' : `${frac >= 0 ? '+' : ''}${(frac * 100).toFixed(d)} pp`);
const fmtMWh = (n) => (n == null ? '—' : `${Number(n).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`);


export default function OmAvailability({ contractId }) {
  const colors = useThemeColors();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL}/api/contracts/${contractId}/om/availability`),
      axios.get(`${API_BASE_URL}/api/contracts/${contractId}/om/reports`),
    ])
      .then(([dash, reps]) => {
        setData(dash.data);
        setReports(reps.data?.reports || []);
        setError(null);
      })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const g    = data?.guarantee || {};
  const cur  = data?.current || null;
  const ga   = g.guaranteed_availability_pct;
  const ld   = cur?.ld_exposure || {};
  const series = useMemo(() => data?.series || [], [data]);
  const status = STATUS[cur?.status] || STATUS.unknown;

  // Chart: cumulative EPEBA per period vs the flat guarantee line.
  const chart = useMemo(() => {
    const pts = series.filter((s) => s.epeba_pct != null);
    const x   = pts.map((s) => s.period_label);
    const eb  = pts.map((s) => +(s.epeba_pct * 100).toFixed(3));
    const gl  = ga != null ? pts.map(() => +(ga * 100).toFixed(3)) : [];
    const lo  = Math.min(ga != null ? ga * 100 : 100, ...(eb.length ? eb : [100]));
    return { x, eb, gl, yMin: Math.max(0, Math.floor(lo - 1)), yMax: 100 };
  }, [series, ga]);

  const delReport = (rid) => {
    axios.delete(`${API_BASE_URL}/api/contracts/${contractId}/om/reports/${rid}`)
      .then(() => load())
      .catch(() => {});
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={120} sx={{ bgcolor: colors.skeleton }} />
        <Skeleton variant="rounded" height={260} sx={{ bgcolor: colors.skeleton }} />
      </Stack>
    );
  }
  if (error) {
    return <Alert severity="error">Couldn’t load availability tracking: {error}</Alert>;
  }

  const ldPctOfCap = (ld.cap_aud && ld.capped_aud != null)
    ? Math.min(100, (ld.capped_aud / ld.cap_aud) * 100) : 0;

  return (
    <Stack spacing={2.5}>
      {/* Header + guarantee summary */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Warranted Minimum Availability</Typography>
          {ga != null && (
            <Chip size="small" label={`Guarantee ${fmtPct(ga, 0)} · ${(g.basis || 'energy_based').replace('_', '-')}`}
                  sx={{ bgcolor: 'rgba(217,138,43,0.12)', color: AMBER, fontWeight: 600 }} />
          )}
          {g.o_and_m_contractor && (
            <Chip size="small" variant="outlined" label={`Operator: ${g.o_and_m_contractor}`} />
          )}
          {data?.source_mode === 'aemo_indicative' && (
            <Tooltip arrow title="Indicative — auto-derived from AEMO (offered availability vs the resource forecast) for this Operational Year. A proxy for the contractual EPEBA (which needs plant SCADA); add the operator's reported EPEBA below and it takes over.">
              <Chip size="small" variant="outlined" label="Indicative · AEMO"
                    sx={{ color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Awaiting-inputs notice */}
      {data?.missing_inputs?.length > 0 && (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Awaiting inputs</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {data.missing_inputs.map((m, i) => <li key={i}><Typography variant="body2">{m}</Typography></li>)}
          </Box>
        </Alert>
      )}

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <KpiCard
          label="Cumulative EPEBA"
          value={cur ? fmtPct(cur.epeba_pct) : '—'}
          accent={status.color}
          placeholder={!cur}
          tooltip={data?.source_mode === 'aemo_indicative'
            ? 'Indicative energy-based availability — AEMO-derived (offered availability vs the resource forecast), cumulative over the Operational Year. Overridden by the operator’s reported EPEBA.'
            : 'Operator-reported energy-based availability, cumulative over the current Operational Year.'}
        />
        <KpiCard
          label={`Headroom vs ${ga != null ? fmtPct(ga, 0) : 'target'}`}
          value={cur ? fmtPP(cur.headroom_pct) : '—'}
          accent={status.color}
          placeholder={!cur}
          tooltip="Percentage points above (or below) the Guaranteed Availability Level."
        />
        <KpiCard
          label="LD exposure"
          value={ld.computable ? fmtAUDCompact(ld.capped_aud) : '—'}
          unit={ld.is_capped ? '(capped)' : ''}
          accent={ld.capped_aud > 0 ? '#ef4444' : '#22c55e'}
          placeholder={!ld.computable}
          tooltip={`Availability LD = (GA − EPEBA) × BEP × (EPEE + EPLE); never below zero, capped at 100% of the O&M fee.`
            + `${g.bep_aud_per_mwh != null ? ` BEP $${g.bep_aud_per_mwh}/MWh.` : ''}`
            + `${g.ld_cap_aud != null ? ` Cap ${fmtAUDCompact(g.ld_cap_aud)}.` : ''}`}
        />
        <KpiCard
          label="Status"
          value={status.label}
          accent={status.color}
          tooltip={cur ? `Operational Year ${cur.operational_year} · as of ${cur.as_of}` : 'No reports yet'}
        />
      </Box>

      {/* Chart: cumulative EPEBA vs guarantee */}
      <Card sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Cumulative EPEBA vs guarantee{cur ? ` — Operational Year ${cur.operational_year}` : ''}
        </Typography>
        {chart.x.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2">No availability reports yet — add one below to start tracking.</Typography>
          </Box>
        ) : (
          <LineChart
            height={280}
            xAxis={[{ scaleType: 'point', data: chart.x }]}
            yAxis={[{ min: chart.yMin, max: chart.yMax, valueFormatter: (v) => `${v}%` }]}
            series={[
              { data: chart.eb, label: 'Cumulative EPEBA', color: AMBER, curve: 'linear', showMark: true },
              ...(chart.gl.length
                ? [{ data: chart.gl, label: `Guarantee ${fmtPct(ga, 0)}`, color: '#ef4444', curve: 'linear', showMark: false }]
                : []),
            ]}
            margin={{ left: 50, right: 16, top: 16, bottom: 28 }}
            slotProps={{ legend: { ...defaultLineLegendProps } }}
          />
        )}
      </Card>

      {/* LD exposure breakdown */}
      {ld.computable && (
        <Card sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>Liquidated-damages exposure</Typography>
            <Tooltip arrow title={'Availability Liquidated Damages = (GA − EPEBA) × BEP × (EPEE + EPLE). '
              + `GA = Guaranteed Availability Level${ga != null ? ` (${fmtPct(ga, 0)})` : ''}; `
              + 'EPEBA = Evaluation-Period Energy-Based Availability; '
              + `BEP = Bundled Energy Price${g.bep_aud_per_mwh != null ? ` ($${g.bep_aud_per_mwh}/MWh)` : ''}; `
              + 'EPEE = Evaluation-Period Expected Energy (MWh); EPLE = Evaluation-Period Lost Energy (MWh). '
              + `Never less than zero; capped at 100% of the O&M fee${g.annual_om_fee_aud != null ? ` (${fmtAUDCompact(g.annual_om_fee_aud)})` : ''}.`}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Uncapped</Typography>
              <Typography variant="h6">{fmtAUDCompact(ld.uncapped_aud)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Capped (payable)</Typography>
              <Typography variant="h6" sx={{ color: ld.capped_aud > 0 ? '#ef4444' : 'text.primary' }}>
                {fmtAUDCompact(ld.capped_aud)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Cap (100% of O&M fee)</Typography>
              <Typography variant="h6">{fmtAUDCompact(ld.cap_aud)}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 160, alignSelf: 'center' }}>
              <LinearProgress variant="determinate" value={ldPctOfCap}
                sx={{ height: 8, borderRadius: 4,
                  '& .MuiLinearProgress-bar': { backgroundColor: ld.is_capped ? '#ef4444' : AMBER } }} />
              <Typography variant="caption" color="text.secondary">
                {ld.is_capped ? 'Exposure exceeds the cap — payable is capped.' : `${ldPctOfCap.toFixed(0)}% of cap`}
              </Typography>
            </Box>
          </Stack>
        </Card>
      )}

      {/* Reports table */}
      <Card sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Operator reports</Typography>
        {reports.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1.5 }}>None yet.</Typography>
        ) : (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>OY</TableCell><TableCell>Period</TableCell>
                <TableCell align="right">EPEBA</TableCell>
                <TableCell align="right">EPEE (MWh)</TableCell>
                <TableCell align="right">EPLE (MWh)</TableCell>
                <TableCell>Source</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const eb = r.epeba_pct != null ? (r.epeba_pct > 1 ? r.epeba_pct / 100 : r.epeba_pct)
                         : (r.eple_mwh != null && r.epme_mwh != null && (r.epme_mwh + r.eple_mwh) > 0
                            ? 1 - r.eple_mwh / (r.epme_mwh + r.eple_mwh) : null);
                const breach = ga != null && eb != null && eb < ga;
                return (
                  <TableRow key={r.report_id} hover>
                    <TableCell>{r.operational_year}</TableCell>
                    <TableCell>{r.period_label}</TableCell>
                    <TableCell align="right" sx={{ color: breach ? '#ef4444' : 'inherit', fontWeight: breach ? 600 : 400 }}>
                      {fmtPct(eb)}
                    </TableCell>
                    <TableCell align="right">{fmtMWh(r.epee_mwh)}</TableCell>
                    <TableCell align="right">{fmtMWh(r.eple_mwh)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={(r.source || '').replace('_', ' ')} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => delReport(r.report_id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          <AlertTitle>Requires SCADA data</AlertTitle>
          The availability above is <strong>indicative</strong> — auto-derived from AEMO (offered
          availability vs the resource forecast). The contractual EPEBA can’t be computed without plant
          data: per-equipment fault/alarm codes (inverter, combiner box, MV feeder, tracker), met-station
          weather for the expected-energy model, and the revenue meter. Once SCADA access is granted,
          availability and liquidated damages are calculated automatically.
        </Alert>
      </Card>
    </Stack>
  );
}
